import { parse } from 'messageformat-parser';
import plurals from 'make-plural/umd/plurals';

// const isDevelopment = process.env.NODE_ENV !== 'production'
// Fix interpolation in production build
const isDevelopment = true;

const isString = s => typeof s === 'string';
const isFunction = f => typeof f === 'function';
function isEmpty(obj) {
    // null and undefined are "empty"
    if (obj === null || obj === undefined) return true;

    if (obj.length > 0) return false;
    if (obj.length === 0) return true;

    return !Object.getOwnPropertyNames(obj).length;
}

function date(locales, format = {}) {
    const formatter = new Intl.DateTimeFormat(locales, format);
    return value => {
        if (isString(value)) value = new Date(value);
        return formatter.format(value);
    };
}

function number(locales, format = {}) {
    const formatter = new Intl.NumberFormat(locales, format);
    return value => formatter.format(value);
}

const defaultFormats = (language, locales, languageData = {}, formats = {}) => {
    locales = locales || language;
    const { plurals } = languageData;
    const style = format =>
        isString(format) ? formats[format] || { style: format } : format;
    const replaceOctothorpe = (value, message) => {
        return ctx => {
            const msg = isFunction(message) ? message(ctx) : message;
            const norm = Array.isArray(msg) ? msg : [msg];
            const formatter = new Intl.NumberFormat(locales);
            const valueStr = formatter.format(value);
            return norm.map(m => (isString(m) ? m.replace('#', valueStr) : m));
        };
    };

    return {
        plural: (value, { offset = 0, ...rules }) => {
            const message = rules[value] || rules[plurals(value - offset)];
            return replaceOctothorpe(value - offset, message);
        },

        selectordinal: (value, { offset = 0, ...rules }) => {
            const message =
                rules[value] || rules[plurals(value - offset, true)];
            return replaceOctothorpe(value - offset, message);
        },

        select: (value, rules) => rules[value] || rules.other,

        number: (value, format) => number(locales, style(format))(value),

        date: (value, format) => date(locales, style(format))(value),

        undefined: value => value
    };
};

// Params -> CTX
/**
 * Creates a context object, which formats ICU MessageFormat arguments based on
 * argument type.
 *
 * @param language     - Language of message
 * @param locales      - Locales to be used when formatting the numbers or dates
 * @param values       - Parameters for variable interpolation
 * @param languageData - Language data (e.g: plurals)
 * @param formats - Custom format styles
 * @returns {function(string, string, any)}
 */
function context({ language, locales, values, formats, languageData }) {
    const formatters = defaultFormats(language, locales, languageData, formats);

    const ctx = (name, type, format) => {
        const value = values[name];
        const formatted = formatters[type](value, format);
        const message = isFunction(formatted) ? formatted(ctx) : formatted;
        return Array.isArray(message) ? message.join('') : message;
    };

    return ctx;
}

export function interpolate(translation, language, locales, languageData) {
    return (values, formats = {}) => {
        const message = translation(
            context({
                language,
                locales,
                languageData,
                formats,
                values
            })
        );

        return Array.isArray(message) ? message.join('').trim() : message;
    };
}

const flatten = arrays => [].concat.apply([], arrays);
const zip = (a, b) => a.map((item, index) => [item, b[index]]);

const t = (strings, ...values) => {
    return flatten(zip(strings, values)).join('');
};

const _plural = type => i18n => ({
    value,
    offset = 0,
    locales,
    format,
    other,
    ...pluralForms
}) => {
    if (locales === undefined) locales = i18n.locales || i18n.language;

    const diff = value - offset;
    const diffAsString = number(locales, format)(diff);
    const translation =
        pluralForms[value.toString()] || // exact match
        pluralForms[i18n.pluralForm(diff, type)] || // plural form
        other; // fallback
    return translation.replace('#', diffAsString);
};

const plural = _plural('cardinal');
const selectOrdinal = _plural('ordinal');

function select({ value, other, ...selectForms }) {
    return selectForms[value] || other;
}

// [Tokens] -> (CTX -> String)
function processTokens(tokens, octothorpe = {}) {
    if (!tokens.filter(token => !isString(token)).length) {
        return tokens.join('');
    }

    return ctx =>
        tokens.map(token => {
            if (isString(token)) {
                return token;

                // # in plural case
            } else if (token.type === 'octothorpe') {
                const { name, offset = 0 } = octothorpe;
                return ctx(name) - offset;

                // simple argument
            } else if (token.type === 'argument') {
                return ctx(token.arg);

                // argument with custom format (date, number)
            } else if (token.type === 'function') {
                return ctx(token.arg, token.key, token.params[0]);
            }

            const offset = token.offset ? parseInt(token.offset) : undefined;

            // complex argument with cases
            const formatProps = {};
            token.cases.forEach(item => {
                formatProps[item.key] = processTokens(item.tokens, {
                    name: token.arg,
                    offset
                });
            });

            return ctx(token.arg, token.type, {
                offset,
                ...formatProps
            });
        });
}

// Message -> (Params -> String)
function compile(message) {
    try {
        return processTokens(parse(message));
    } catch (e) {
        console.error(
            `Message cannot be parsed due to syntax errors: ${message}`
        );
        return message;
    }
}

const loadLanguageData = locale => {
    if (!locale) {
        return;
    }

    const [language] = locale.split(/[_-]/);
    return { plurals: plurals[language] };
};

var dev = /*#__PURE__*/ Object.freeze({
    compile: compile,
    loadLanguageData: loadLanguageData
});

function getLanguageData(catalog) {
    return (catalog || {}).languageData || {};
}

function getMessages(catalog) {
    return (catalog || {}).messages || {};
}

class I18n {
    _language;
    _locales;

    // Message catalogs
    _catalogs;

    _missing;

    // Messages/language data in active language.
    // This is optimization, so we don't perform object lookup
    // _catalogs[language] for each translation.
    _activeMessages;
    _activeLanguageData;

    _dev;

    t;
    plural;
    select;
    selectOrdinal;

    date;
    number;

    constructor() {
        // Messages and languageData are merged on load,
        // so we must initialize it manually
        this._activeMessages = {};
        this._catalogs = {};

        if (isDevelopment) {
            this.t = t;
            this.select = select;
            this.plural = plural(this);
            this.selectOrdinal = selectOrdinal(this);
        }
    }

    get availableLanguages() {
        return Object.keys(this._catalogs);
    }

    get language() {
        return this._language;
    }

    get locales() {
        return this._locales;
    }

    get messages() {
        return this._activeMessages;
    }

    get languageData() {
        return this._activeLanguageData;
    }

    _cacheActiveLanguage() {
        const activeCatalog = this._catalogs[this.language];

        let languageData = getLanguageData(activeCatalog);
        if (isDevelopment) {
            // Allow overriding data in development, useful for testing
            if (
                isEmpty(languageData) &&
                this._dev &&
                isFunction(this._dev.loadLanguageData)
            ) {
                languageData = this._dev.loadLanguageData(this.language);
            }
        }

        this._activeMessages = getMessages(activeCatalog);
        this._activeLanguageData = languageData;
    }

    load(catalogs) {
        if (typeof catalogs !== 'object') return;

        // deeply merge Catalogs
        Object.keys({ ...this._catalogs, ...catalogs }).forEach(language => {
            let compiledMessages = getMessages(catalogs[language]);

            if (isDevelopment) {
                if (this._dev && isFunction(this._dev.compile)) {
                    compiledMessages = Object.keys(compiledMessages).reduce(
                        (dict, id) => {
                            const msg = compiledMessages[id];
                            dict[id] = isString(msg)
                                ? this._dev.compile(msg)
                                : msg;
                            return dict;
                        },
                        {}
                    );
                }
            }

            this._catalogs[language] = {
                messages: {
                    ...getMessages(this._catalogs[language]),
                    ...compiledMessages
                },
                languageData: {
                    ...getLanguageData(this._catalogs[language]),
                    ...getLanguageData(catalogs[language])
                }
            };
        });

        this._cacheActiveLanguage();
    }

    activate(language, locales) {
        if (!language) return;

        if (isDevelopment) {
            if (this.availableLanguages.indexOf(language) === -1) {
                console.warn(
                    `Message catalog for locale "${language}" not loaded.`
                );
            }
        }

        this._language = language;
        this._locales = locales;
        this._cacheActiveLanguage();
    }

    use(language, locales) {
        return setupI18n({
            language,
            locales,
            catalogs: this._catalogs,
            development: this._dev
        });
    }

    // default translate method
    _(id, values, { defaults, formats = {} } = {}) {
        // Expand message descriptor
        if (id && typeof id === 'object') {
            values = id.values;
            defaults = id.defaults;
            formats = id.formats;
            id = id.id;
        }

        let translation = this.messages[id] || defaults || id;

        // replace missing messages with custom message for debugging
        const missing = this._missing;
        if (missing && !this.messages[id]) {
            translation = isFunction(missing)
                ? missing(this.language, id)
                : missing;
        }

        if (isDevelopment) {
            if (
                isString(translation) &&
                this._dev &&
                isFunction(this._dev.compile)
            ) {
                translation = this._dev.compile(translation);
            }
        }

        if (!isFunction(translation)) return translation;
        return interpolate(
            translation,
            this.language,
            this.locales,
            this.languageData
        )(values, formats);
    }

    pluralForm(n, pluralType = 'cardinal') {
        if (!this.languageData.plurals) return 'other';
        return this.languageData.plurals(n, pluralType === 'ordinal');
    }

    date(value, format) {
        return date(this.locales || this.language, format)(value);
    }

    number(value, format) {
        return number(this.locales || this.language, format)(value);
    }
}

function setupI18n(params = {}) {
    const i18n = new I18n();

    if (isDevelopment) {
        i18n._dev = dev;
    }

    if (params.catalogs) i18n.load(params.catalogs);
    if (params.language) i18n.activate(params.language, params.locales);
    if (params.missing) i18n._missing = params.missing;

    return i18n;
}

const i18n = setupI18n();

const i18nMark = id => id;

export { date, number, i18n, setupI18n, i18nMark };
